import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LayoutAdminHeader from '../components/LayoutAdminHeader';
import LayoutAdminFooter from '../components/LayoutAdminFooter';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const GSTPercentageList = () => {
    const [gstList, setGstList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [percentageValue, setPercentageValue] = useState('');

    const fetchGSTs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");

const res = await axios.get(`${BASE_URL}/GSTPercentage/GetAllGsts`, {
  headers: {
    Authorization: `Bearer ${token}`,
  },
});
            const data = res.data.data || [];

            const formatted = data.map((item, index) => ({
                ...item,
                id: item._id || index,
            }));

            setGstList(formatted);
            setFilteredList(formatted);
        } catch (err) {
            console.error('Failed to fetch GST percentages:', err);
            toast.error('Failed to fetch GST percentages');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGSTs();
    }, []);

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearch(value);
        const filtered = gstList.filter(item =>
            item.PercentageValue.toString().includes(value)
        );
        setFilteredList(filtered);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this GST percentage?')) return;
        try {
            const token = localStorage.getItem("token");

const res = await axios.delete(`${BASE_URL}/GSTPercentage/DeleteGst/${id}`, {
  headers: {
    Authorization: `Bearer ${token}`,
  },
});
            toast.success('GST percentage deleted successfully');
            fetchGSTs();
        } catch (err) {
            toast.error('Failed to delete GST percentage');
        }
    };

    const handleEdit = (row) => {
        setSelected(row);
        setPercentageValue(row.PercentageValue);
        setOpen(true);
    };

    const handleAdd = () => {
        setSelected(null);
        setPercentageValue('');
        setOpen(true);
    };

    const checkGSTExists = async (value) => {
        try {
            const token = localStorage.getItem("token");

const res = await axios.get(
  `${BASE_URL}/GSTPercentage/CheckGstExists/${value}`,
  {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);
            return res.data;
        } catch (err) {
            console.error('Check GST exists error:', err);
            return { success: false, exists: false, message: 'Failed to check GST existence' };
        }
    };

    const handleSave = async () => {
        if (!percentageValue || isNaN(percentageValue)) {
            toast.error('Please enter a valid percentage value');
            return;
        }

        const data = {
            PercentageValue: parseFloat(percentageValue),
        };

        try {
            // Check if GST percentage already exists
            const checkResult = await checkGSTExists(percentageValue);
            if (checkResult.exists && (!selected || selected.PercentageValue !== parseFloat(percentageValue))) {
                toast.error(checkResult.message || 'GST Percentage Already Exists');
                return;
            }

            if (selected) {
                const token = localStorage.getItem("token");

const res = await axios.put(
  `${BASE_URL}/GSTPercentage/UpdateGst/${selected._id}`,
  data,
  {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);
                toast.success('GST percentage updated successfully');
            } else {
                const token = localStorage.getItem("token");

const res = await axios.post(
  `${BASE_URL}/GSTPercentage/AddGst`,
  data,
  {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);
                toast.success('GST percentage added successfully');
            }

            setOpen(false);
            fetchGSTs();
        } catch (err) {
            console.error('Save failed:', err);
            toast.error(err.response?.data?.message || 'Failed to save GST percentage');
        }
    };

    const columns = [
        {
            field: 'PercentageValue',
            headerName: 'Percentage (%)',
            flex: 1,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <>
                    <IconButton color="primary" onClick={() => handleEdit(params.row)}>
                        <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDelete(params.row._id)}>
                        <DeleteIcon />
                    </IconButton>
                </>
            ),
        },
    ];

    return (
        <>
            <LayoutAdminHeader />
            <Box p={2}>
                <Typography variant="h5" gutterBottom>
                    GST Percentage Management
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={2}>
                    <TextField
                        label="Search"
                        value={search}
                        onChange={handleSearch}
                        placeholder="Search by percentage"
                        fullWidth
                    />
                    <Button variant="contained" color="primary" onClick={handleAdd} sx={{ ml: 2 }}>
                        Add GST
                    </Button>
                </Box>

                <DataGrid
                    autoHeight
                    rows={filteredList}
                    columns={columns}
                    pageSize={5}
                    rowsPerPageOptions={[5, 10]}
                    loading={loading}
                    disableSelectionOnClick
                />

                <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>{selected ? 'Edit GST Percentage' : 'Add GST Percentage'}</DialogTitle>
                    <DialogContent>
                        <TextField
                            margin="dense"
                            label="GST Percentage (%)"
                            fullWidth
                            type="number"
                            value={percentageValue}
                            onChange={(e) => setPercentageValue(e.target.value)}
                            required
                            inputProps={{ step: "0.01" }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleSave}>
                            {selected ? 'Update' : 'Add'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            {/* <LayoutAdminFooter /> */}
        </>
    );
};

export default GSTPercentageList;